import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { Router, RouterLinkActive, RouterLink } from '@angular/router';
import { Observable, Subject, of } from 'rxjs';
import { shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { SettingsService } from '../../services/settings.service';
import { CompanySettings } from '../../models/settings';
import { User } from '../../users/user.model';
import { LeaveRequestsService } from '../../leave-requests/leave-requests.service';
import { DeviceService } from '../../services/device.service';
import { AsyncPipe } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCalendarDay,
  faCalendarDays,
  faCalendarRange,
  faChartColumn,
  faCircleQuestion,
  faClock,
  faDisplay,
  faGauge,
  faGear,
  faInbox,
  faMobileScreen,
  faUmbrellaBeach,
  faUser,
  faUsers,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';

declare interface RouteInfo {
  path: string;
  title: string;
  icon: string;
  class: string;
  group: RouteGroup;
}

export enum RouteGroup {
  User,
  Administrator,
  Settings,
}
export const ROUTES: RouteInfo[] = [
  {
    path: '/mobile',
    title: 'Mobil',
    icon: 'mobile_friendly',
    class: '',
    group: RouteGroup.User,
  },
  {
    path: '/dashboard',
    title: 'Dashboard',
    icon: 'dashboard',
    class: '',
    group: RouteGroup.Administrator,
  },
  {
    path: '/holidays',
    title: 'Feiertage',
    icon: 'calendar_today',
    class: '',
    group: RouteGroup.Administrator,
  },
  {
    path: '/leave-requests/apply',
    title: 'Urlaub beantragen',
    icon: 'beach_access',
    class: '',
    group: RouteGroup.User,
  },
  {
    path: '/leave-requests/pending',
    title: 'Urlaubsanträge',
    icon: 'inbox',
    class: '',
    group: RouteGroup.User,
  },
  {
    path: '/working-time-rule-sets',
    title: 'Arbeitszeitmodelle',
    icon: 'access_time',
    class: '',
    group: RouteGroup.Administrator,
  },
  {
    path: '/working-hours-overview',
    title: 'Arbeitszeitübersicht',
    icon: 'calendar_view_day',
    class: '',
    group: RouteGroup.Administrator,
  },
  {
    path: '/settings',
    title: 'Einstellungen',
    icon: 'settings',
    class: '',
    group: RouteGroup.Settings,
  },
  {
    path: '/users',
    title: 'Benutzer',
    icon: 'group',
    class: '',
    group: RouteGroup.Settings,
  },
  {
    path: '/devices',
    title: 'Geräte',
    icon: 'devices',
    class: '',
    group: RouteGroup.Settings,
  },
];

@Component({

    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    standalone: true,
    imports: [
        RouterLinkActive,
        RouterLink,
        AsyncPipe,
      FaIconComponent,
    ],
})
export class SidebarComponent implements OnInit, OnDestroy {
  userMenuItems: {
    path: string;
    title: string;
    icon: string;
    class: string;
    group: RouteGroup;
  }[];
  filteredUserMenuItems: {
    path: string;
    title: string;
    icon: string;
    class: string;
    group: RouteGroup;
  }[];
  adminMenuItems: {
    path: string;
    title: string;
    icon: string;
    class: string;
    group: RouteGroup;
  }[];
  settingsMenuItems: {
    path: string;
    title: string;
    icon: string;
    class: string;
    group: RouteGroup;
  }[];

  private _subject$ = new Subject();
  public isAdministrator?: boolean;
  public user?: User;
  public companySettings$?: Observable<CompanySettings | undefined>;
  public pendingLeaveRequests$!: Observable<number | null>;
  private leaveRequestsEnabled = true;
  public canApproveLeaveRequests = false;
  public isMobileDevice = false;
  private readonly iconMap: Record<string, IconDefinition> = {
    close: faXmark,
    person: faUser,
    bar_chart: faChartColumn,
    calendar_view_day: faCalendarDay,
    event_note: faCalendarDays,
    date_range: faCalendarRange,
    today: faCalendarDay,
    mobile_friendly: faMobileScreen,
    dashboard: faGauge,
    calendar_today: faCalendarDay,
    beach_access: faUmbrellaBeach,
    inbox: faInbox,
    access_time: faClock,
    settings: faGear,
    group: faUsers,
    devices: faDisplay,
  };
  private readonly fallbackIcon = faCircleQuestion;

  constructor(
    private authService: AuthService,
    private settingsService: SettingsService,
    private router: Router,
    private leaveRequestsService: LeaveRequestsService,
    private deviceService: DeviceService
  ) {
    this.userMenuItems = ROUTES.filter(
      (menuItem) => menuItem.group === RouteGroup.User
    );
    this.filteredUserMenuItems = this.userMenuItems;
    this.adminMenuItems = ROUTES.filter(
      (menuItem) => menuItem.group === RouteGroup.Administrator
    );
    this.settingsMenuItems = ROUTES.filter(
      (menuItem) => menuItem.group === RouteGroup.Settings
    );

    this.pendingLeaveRequests$ = this.leaveRequestsService.pendingCount$;
  }

  protected icon(name: string): IconDefinition {
    return this.iconMap[name] ?? this.fallbackIcon;
  }

  ngOnInit() {
    this.isMobileDevice = this.deviceService.isMobileDevice();
    this.updateUserMenuItems();
    this.authService.user
      .pipe(takeUntil(this._subject$))
      .subscribe((user) => {
        this.user = user;
        this.isAdministrator = user ? user.isAdministrator : false;
        this.canApproveLeaveRequests = false;
        this.updateUserMenuItems();
        if (user && this.leaveRequestsEnabled) {
          this.checkApproverStatus();
        } else {
          this.leaveRequestsService.resetPendingCount();
          this.canApproveLeaveRequests = false;
          this.updateUserMenuItems();
        }
      });

    this.companySettings$ = this.authService.loggedIn.pipe(
      switchMap((loggedIn) =>
        loggedIn
          ? this.settingsService.get()
          : of(undefined)
      ),
      shareReplay(1),
      takeUntil(this._subject$)
    );

    this.authService.loggedIn
      .pipe(
        switchMap((loggedIn) =>
          loggedIn ? this.settingsService.full() : of(undefined)
        ),
        takeUntil(this._subject$)
      )
      .subscribe((settings) => {
        if (!settings) {
          this.leaveRequestsEnabled = false;
          this.canApproveLeaveRequests = false;
          this.leaveRequestsService.resetPendingCount();
          this.updateUserMenuItems();
          return;
        }

        this.leaveRequestsEnabled = settings?.leaveRequests?.enabled !== false;
        if (!this.leaveRequestsEnabled) {
          this.canApproveLeaveRequests = false;
        }
        this.updateUserMenuItems();

        if (!this.leaveRequestsEnabled) {
          this.leaveRequestsService.resetPendingCount();
        } else if (this.user) {
          this.checkApproverStatus();
        }
      });
  }

  isMobileMenu(): boolean {
    return this.deviceService.isViewportWidthBelow(991);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobileDevice = this.deviceService.isMobileDevice();
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  closeMobileSidebar(event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
    }
    if (document.body.classList.contains('nav-open')) {
      const toggleButton = document.querySelector(
        '.navbar-toggler'
      ) as HTMLButtonElement | null;
      toggleButton?.click();
    }
  }

  ngOnDestroy(): void {
    this._subject$.next(true);
    this._subject$.complete();
  }

  private updateUserMenuItems(): void {
    this.filteredUserMenuItems = this.userMenuItems.filter(
      (menuItem) =>
        (this.leaveRequestsEnabled &&
          (menuItem.path !== '/leave-requests/pending' || this.canApproveLeaveRequests)) ||
        menuItem.path.startsWith('/leave-requests') === false
    );
  }

  private checkApproverStatus(): void {
    this.leaveRequestsService
      .isApprover()
      .pipe(takeUntil(this._subject$))
      .subscribe({
        next: (canApprove: boolean) => {
          this.canApproveLeaveRequests = canApprove;
          if (canApprove) {
            this.leaveRequestsService.refreshPendingCount();
          } else {
            this.leaveRequestsService.resetPendingCount();
          }
          this.updateUserMenuItems();
        },
        error: () => {
          this.canApproveLeaveRequests = false;
          this.leaveRequestsService.resetPendingCount();
          this.updateUserMenuItems();
        },
      });
  }
}
